import type { RouteLocationRaw } from "vue-router";
import i18n from "@/i18n";
import {
	API_ERROR_CODES,
	ApiCallError,
	type AcceptInvitationResponse,
	type InvitationDelivery,
	type InvitationKind,
} from "@/api";
import { errorMessage } from "@/shared/utils/errors";
import { restrictionError } from "@/shared/utils/spaceErrors";

/*
  The rules every invitation surface shares, apart from any view so they can be tested: where an
  invitation leads, how a refusal is worded, what a create route's answer is split into, and how
  long an invitation has left. The inbox (Account, the user menu, the home banners), the token page
  and the three invite dialogs all say the same things, in the same words.
*/

/**
 * Where the project, dataspace or department an invitation is to is opened. The Designer's project
 * home, the Operator's entry for a dataspace (which decides between its start page and its process
 * list), the department's own page.
 */
export function invitationTarget(kind: InvitationKind, id: string): RouteLocationRaw {
	switch (kind) {
		case "project":
			return { name: "project-home", params: { projectId: id } };
		case "dataspace":
			return `/operator/${id}`;
		case "department":
			return { name: "department", params: { departmentId: id } };
	}
}

/** Where an accepted invitation leads: the space or the department the person has joined. */
export function acceptedTarget(accepted: AcceptInvitationResponse): RouteLocationRaw {
	switch (accepted.kind) {
		case "project":
			return invitationTarget("project", accepted.project_id);
		case "dataspace":
			return invitationTarget("dataspace", accepted.dataspace_id);
		case "department":
			return invitationTarget("department", accepted.department_id);
	}
}

/** A role's display name — a project's, a dataspace's or a department's: the built-in ones are translated. */
export function roleLabel(name: string): string {
	const { t, te } = i18n.global;
	const key = `roles.names.${name}`;
	return te(key) ? t(key) : name;
}

/**
 * Whole days an invitation has left, rounded up so the last day still says "1 day"; zero or less
 * once it has run out. Expiry is a function of time, so every reader applies it alike.
 */
export function daysLeft(expiresAt: string, now = Date.now()): number {
	return Math.ceil((new Date(expiresAt).getTime() - now) / 86_400_000);
}

/** "Expires in 3 days", or "Expired". */
export function expiryLabel(expiresAt: string, now = Date.now()): string {
	const { t } = i18n.global;
	const days = daysLeft(expiresAt, now);
	return days <= 0 ? t("invitations.expiry.expired") : t("invitations.expiry.in", { n: days });
}

/** Whether an invitation in an inviter's list is one its invitee turned down. */
export function isDeclined(invitation: { status: string }): boolean {
	return invitation.status === "declined";
}

/**
 * What a create route answered, split: the invitation as it belongs in a list, and what became of
 * its link. The link is in that one answer and nowhere else, so it must not stay on the object a
 * store keeps — the dialog that asked for it holds it until it is closed.
 */
export function splitDelivery<T extends InvitationDelivery>(
	created: T,
): { invitation: Omit<T, keyof InvitationDelivery>; mailSent: boolean; acceptUrl: string | null } {
	const { mail_sent, accept_url, ...invitation } = created;
	return { invitation, mailSent: mail_sent, acceptUrl: mail_sent ? null : (accept_url ?? null) };
}

/**
 * The message for a refused invitation request from the sending side — a create, a resend, a revoke
 * or a dismissal: ours for the codes that have words (`invitations.errors`), the numbers for a
 * personal space's member limit, else whatever came. `invitation_mail_disabled` is the one a
 * resend meets while invitation mail is off.
 */
export function invitationError(e: unknown): string {
	const limited = restrictionError(e);
	if (limited) return limited;
	if (e instanceof ApiCallError && e.code) {
		const { t, te } = i18n.global;
		const key = `invitations.errors.${e.code}`;
		if (te(key)) return t(key);
	}
	return errorMessage(e);
}

/** What went wrong answering an invitation, and whether the inbox is out of date because of it. */
export interface InboxFailure {
	message: string;
	/** Read the inbox again: the invitation is gone, or what the page knew of the account has changed. */
	stale: boolean;
}

/**
 * The words for an invitation answered from the inbox that was refused, by `code` and status:
 *
 *  - `403 email_not_verified`: the provider has not verified the address (the inbox lists nothing
 *    then, and says so);
 *  - `409 personal_member_limit`: the personal space has no room for one more, said with the
 *    numbers, from the invitee's side — they can only ask for room, and the invitation stays pending;
 *  - `409 not_a_department_member`: the space is a department's they are not in — they join the
 *    department first, and the invitation stays pending;
 *  - `404` and `410`: the invitation is gone (withdrawn, expired, or answered in another tab).
 *
 * Anything else is the server's message, and the list is left as it is.
 */
export function inboxFailure(e: unknown): InboxFailure {
	const { t } = i18n.global;
	const limited = restrictionError(e, { audience: "invitee" });
	if (limited) return { message: limited, stale: false };
	if (e instanceof ApiCallError) {
		if (e.code === API_ERROR_CODES.emailNotVerified) {
			return { message: t("invitations.inbox.errors.emailNotVerified"), stale: true };
		}
		if (e.code === API_ERROR_CODES.notADepartmentMember) {
			return { message: t("invitations.inbox.errors.notADepartmentMember"), stale: false };
		}
		if (e.status === 404 || e.status === 410) {
			return { message: t("invitations.inbox.errors.gone"), stale: true };
		}
	}
	return { message: errorMessage(e), stale: false };
}
